import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EquipmentModel, EquipmentService } from 'src/app/core/management-system';

@Component({
  selector: 'app-print-e-plan',
  templateUrl: './print-e-plan.component.html',
  styleUrls: ['./print-e-plan.component.css'],
  standalone: false,
})
export class PrintEPlanComponent implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private equipmentService: EquipmentService,
    private changeDetector: ChangeDetectorRef
  ) {}

  equipmentsTotal: EquipmentModel[] = [];
  equipmentsSearch: EquipmentModel[] = [];
  collectionSize = 0;
  filter = '';
  sortColumn = '';
  sortDirection = '';
  customerTypeCompany = '';

  ngOnInit(): void {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    this.sortColumn = queryParams.get('sortColumn') || '';
    this.sortDirection = queryParams.get('sortDirection') || '';
    this.filter = queryParams.get('s') || '';
    this.customerTypeCompany = queryParams.get('s2') || '';
    this.getDataTable();
  }

  private getDataTable(): void {
    this.equipmentService.getAllEquipmentsForPlan().subscribe((res) => {
      this.equipmentsTotal = res.items || [];
      this.applyFiltersAndSort();

      // Print only after the browser has actually PAINTED the filtered rows.
      // A single rAF fires before the paint that commits the just-inserted @for
      // rows, so window.print() would snapshot the DOM mid-paint — the table
      // shows only its header (blank first page) until a later repaint (e.g.
      // cancelling the dialog) forces the rows in. Waiting for a second rAF
      // guarantees the first frame's paint is done before we open the dialog.
      this.changeDetector.detectChanges();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.print())
      );
    });
  }

  private applyFiltersAndSort(): void {
    const term = this.filter.trim().toLocaleLowerCase();
    let results = this.equipmentsTotal.filter((equipment) =>
      !this.customerTypeCompany ||
      !equipment.workArea ||
      equipment.workArea.name === this.customerTypeCompany
    );

    if (term) {
      results = results.filter((equipment) => this.matchesSearch(equipment, term));
    }

    if (this.sortColumn && this.sortDirection) {
      results = [...results].sort((a, b) => {
        const left = this.getSortValue(a, this.sortColumn);
        const right = this.getSortValue(b, this.sortColumn);
        const comparison = left < right ? -1 : left > right ? 1 : 0;
        return this.sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    this.equipmentsSearch = results;
    this.collectionSize = results.length;
  }

  private matchesSearch(equipment: EquipmentModel, term: string): boolean {
    // Match the same fields the list and plan tables show — including the OIC text
    // from the registration record — so the printed rows equal the on-screen rows.
    const values = [
      equipment.no,
      equipment.name,
      equipment.model,
      equipment.workArea?.name,
      equipment.division,
      equipment.responsiblePersonName,
    ];
    return values.some((value) => (value || '').toLocaleLowerCase().includes(term));
  }

  private getSortValue(equipment: EquipmentModel, column: string): string | number {
    const dateColumns = [
      'calibrationNextDate',
      'calibrationNextDateTime',
      'verificationNextDate',
      'vNextDateTime',
      'icNextDate',
      'icNextDateTime',
      'maintenanceNextDate',
      'maNextDateTime',
      'lastUpdateDateTime',
    ];
    const values: { [key: string]: unknown } = {
      no: equipment.no,
      name: equipment.name,
      model: equipment.model,
      'workArea.name': equipment.workArea?.name,
      c: equipment.c,
      v: equipment.v,
      ic: equipment.ic,
      calibrationNextDate: equipment.calibrationNextDateTime,
      calibrationNextDateTime: equipment.calibrationNextDateTime,
      verificationNextDate: equipment.vNextDateTime,
      vNextDateTime: equipment.vNextDateTime,
      icNextDate: equipment.icNextDateTime,
      icNextDateTime: equipment.icNextDateTime,
      maintenanceNextDate: equipment.maNextDateTime,
      maNextDateTime: equipment.maNextDateTime,
      lastUpdateDateTime: equipment.lastUpdateDateTime,
    };
    const value = values[column];

    if (dateColumns.includes(column)) {
      return value ? new Date(value as string).getTime() : 0;
    }
    if (typeof value === 'number') return value;
    return (value || '').toString().toLocaleLowerCase();
  }

  private print(): void {
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');
    style.type = 'text/css';
    style.media = 'print';
    style.appendChild(document.createTextNode('@page { size: landscape; margin: 0in; }'));
    head.appendChild(style);
    window.print();
  }
}
